import { isEqual } from 'lodash-es';
import { DateTime } from 'luxon';
import { OrderFormData } from '~/api/orders/_types';
import { API_DATE_FORMAT } from '~/constants/_constants';
import browserStorage from '~/plugins/browserStorage';
import { DeepPartial, DeepPartialObject } from '~/utils/_types';
import { defaultErrorMap, string, z } from 'zod';
import { ContactSimpleSchema } from '~/api/contacts/_types';
import { CategoryItemDetail, PartnerSimpleSchema, Price } from '~/api/links/_types';
import { zNullishDefault } from '~/plugins/zod/utils';
import { AddressSimpleSchema } from '~/api/partners/_types';

// @plachtova: objekt vhodny pro cache do browser storage
// data z browser storage timto muzeme jednoduse zvalidovat, jinak je zahodime
const OrderDraftCachedDetailSchema = z.object({
    Order: z.object({
        Id: z.number().nullish(),
        PartnerId: PartnerSimpleSchema,
        ContactId: ContactSimpleSchema.nullish(),
        ReferenceNumber: z.string().nullish(),
        RequiredAt: string().nullish(),
        AddressBillingId: AddressSimpleSchema.nullish(),
        AddressShippingId: AddressSimpleSchema.nullish(),
        HasOtherAddressShipping: z.boolean(),
        Note: z.string().nullish(),
    }),
    Lines: zNullishDefault(
        z
            .object({
                Id: z.string(),
                // pouze pro FE, custom schema bude skipovat validaci pro tuto property
                Item: z.custom<CategoryItemDetail>(),
                Quantity: z.number().min(1),
                RequiredAt: string().nullish(),
                Configuration: z
                    .object({
                        RuleId: z.number(),
                        OptionId: z.number(),
                    })
                    .array(),
                CategoryId: z.number().nullish(),
                Nomenclature: z.custom<string | null>(),
                PriceCatalogue: z.custom<Price | null>(),
                PriceDistributor: z.custom<Price | null>(),
            })
            .array(),
        []
    ),
});

type OrderDraftCachedDetail = z.infer<typeof OrderDraftCachedDetailSchema>;

export const useOrderCartStore = defineStore('order-cart', () => {
    const STORAGE_KEY = 'order-cart-values';

    const formValues = ref<DeepPartial<OrderFormData>>();
    const loadOrderCart = () => {
        let storageValues: DeepPartialObject<OrderDraftCachedDetail> | null = null;

        try {
            storageValues = JSON.parse(browserStorage.get(STORAGE_KEY));
            if (storageValues == null) return;

            storageValues = OrderDraftCachedDetailSchema.parse(storageValues, { errorMap: defaultErrorMap });
        } catch (err) {
            console.error(`Browser storage order cart values zod transform error:`);
            console.error(`Original data: `, storageValues);
            console.error(`Zod Error: `, err);

            return;
        }

        const newValues = {
            Order:
                storageValues.Order == null
                    ? undefined
                    : {
                          ...storageValues.Order,
                          RequiredAt:
                              storageValues.Order.RequiredAt == null
                                  ? null
                                  : DateTime.fromFormat(storageValues.Order.RequiredAt, API_DATE_FORMAT),
                      },
            Lines: storageValues.Lines?.map((x) => ({
                ...x,
                RequiredAt: x.RequiredAt == null ? null : DateTime.fromFormat(x.RequiredAt, API_DATE_FORMAT),
            })),
        };

        if (isEqual(newValues, formValues.value)) return;

        formValues.value = newValues;
    };
    const setOrderCart = (values: DeepPartial<OrderFormData>) => {
        formValues.value = values;
        const storageValues = {
            Order:
                values.Order == null
                    ? {}
                    : {
                          ...values.Order,
                          RequiredAt: (values.Order.RequiredAt as DateTime<boolean> | null | undefined)?.toFormat(
                              API_DATE_FORMAT
                          ),
                      },
            Lines: values.Lines?.map((x) => ({
                ...x,
                RequiredAt: (x.RequiredAt as DateTime<boolean> | null | undefined)?.toFormat(API_DATE_FORMAT),
            })),
        };

        browserStorage.set(STORAGE_KEY, JSON.stringify(storageValues));
    };
    const reset = () => {
        formValues.value = undefined;
        browserStorage.remove(STORAGE_KEY);
    };

    return {
        formValues,
        loadOrderCart,
        setOrderCart,
        reset,
    };
});
